/* global heap, profitwell */
import isObject from 'lodash/isObject'
import transform from 'lodash/transform'
import isEqual from 'lodash/isEqual'
import * as S from '@sentry/react'
import { BrowserTracing } from '@sentry/tracing'
import * as FullStory from '@fullstory/browser'

import { Config } from '../constants'
const version = require('../../package.json').version

export const initApp = () => {
  Sentry.init()
  Heap.init()

  initIntercom()
  Intercom.boot()
  FullStory.init({
    orgId: 'GSMDG',
    devMode: process.env.FULL_STORY_ENABLED === 'false'
  })
}

export const initIntercom = () => {
  ;(() => {
    var w = window
    var ic = w.Intercom
    if (typeof ic === 'function') {
      ic('reattach_activator')
      ic('update', window.intercomSettings)
    } else {
      var d = document
      var i = () => {
        // eslint-disable-next-line
        i.c(arguments)
      }
      i.q = []
      i.c = (args) => {
        i.q.push(args)
      }
      w.Intercom = i
      const l = () => {
        var s = d.createElement('script')
        s.type = 'text/javascript'
        s.async = true
        s.src = 'https://widget.intercom.io/widget/' + Config.INTERCOM_ID
        var x = d.getElementsByTagName('script')[0]
        x.parentNode.insertBefore(s, x)
      }
      if (w.attachEvent) {
        w.attachEvent('onload', l)
      } else {
        w.addEventListener('load', l, false)
      }
    }
  })()

  window.intercomSettings = {
    app_id: Config.INTERCOM_ID
  }
}

export const Intercom = {
  boot: () => {
    if (!window.Intercom) return
    window.intercomSettings = {
      app_id: Config.INTERCOM_ID
    }
    window.Intercom('boot', window.intercomSettings)
  },
  authenticate: (body) => {
    if (!window.Intercom) return
    window.intercomSettings = { ...window.intercomSettings, ...body }
    window.Intercom('boot', window.intercomSettings)
  },
  update: (body = {}) => {
    if (!window.Intercom) return
    window.intercomSettings = { ...window.intercomSettings, ...body }
    if (Object.keys(body) > 0)
      window.Intercom('update', window.intercomSettings)
    else window.Intercom('update')
  },
  trigger: (action, body = {}) => {
    if (!window.Intercom) return
    window.Intercom('trackEvent', action, body)
  },
  show: () => {
    if (!window.Intercom) return
    window.Intercom('show')
  },
  showNewMessage: (message = '') => {
    if (!window.Intercom) return
    window.Intercom('showNewMessage', message)
  },
  hide: () => {
    if (!window.Intercom) return
    window.Intercom('hide')
  },
  hideLauncher: () => {
    if (!window.Intercom) return
    window.intercomSettings = {
      ...window.intercomSettings,
      hide_default_launcher: true
    }
  },
  showLauncher: () => {
    if (!window.Intercom) return
    window.intercomSettings = {
      ...window.intercomSettings,
      hide_default_launcher: false
    }
  },
  shutdown: () => {
    if (!window.Intercom) return
    window.intercomSettings = {}
    window.Intercom('shutdown')
  }
}

export const PW = {
  authenticate: (id) => profitwell('start', { user_id: id })
}

export const initVisibility = () => {
  let hidden

  if (typeof document.hidden !== 'undefined') {
    hidden = 'hidden'
  } else if (typeof document.msHidden !== 'undefined') {
    hidden = 'msHidden'
  } else if (typeof document.webkitHidden !== 'undefined') {
    hidden = 'webkitHidden'
  }

  if (hidden === undefined) {
    return () => false
  } else {
    return () => document[hidden]
  }
}

export const hidden = initVisibility()

export const difference = (object, base) => {
  const changes = (object, base) =>
    transform(object, (result, value, key) => {
      if (!isEqual(value, base[key])) {
        result[key] =
          isObject(value) && isObject(base[key])
            ? changes(value, base[key])
            : value
      }
    })

  return changes(object, base)
}

export const Heap = {
  init: () => {
    heap.load(process.env.HEAP_ID)
  },
  authenticate: (id) => {
    heap.identify(id, 'ID')
  },
  trigger: (action, body = {}) => {
    heap.track(action, body)
  },
  update: (body = {}) => {
    if (Object.keys(body).length > 0) {
      heap.addUserProperties(body)
    }
  },
  shutdown: () => {
    heap.resetIdentity()
  }
}

export const FB = {
  init: (user = {}) => {
    window.fbq('init', '1094068657414839', user)
    window.fbq('track', 'PageView')
  },
  track: (action, body = {}, metadata = {}) => {
    if (window.fbq) window.fbq('track', action, body, metadata)
  }
}

export const Google = {
  login: (id, type, subscribed) => {
    if (!window.dataLayer) return
    window.dataLayer.push({ event: 'login', id, type, subscribed })
  },
  logout: () => {
    if (!window.dataLayer) return
    window.dataLayer.push({ id: '' })
  },
  track: (event, metadata = {}) => {
    if (!window.dataLayer) return
    window.dataLayer.push({ event, ...metadata })
  }
}

export const Sentry = {
  init: () => {
    S.init({
      dsn: Config.SENTRY_URI,
      release: version,
      integrations: [new BrowserTracing()],
      tracesSampleRate: 0.8
    })
  },
  track: (error, errorInfo, fingerprint) => {
    S.withScope((scope) => {
      if (errorInfo) scope.setExtras(errorInfo)
      if (fingerprint) scope.setFingerprint(['{{default}}', fingerprint])
      S.captureException(error)
    })
  },
  authenticate: (id, email) => {
    S.configureScope((scope) => {
      scope.setUser({ id, ...(email ? { email } : {}) })
    })
  }
}

export const Partners = {
  hsa: () => {
    return window.location.host.includes('hsa')
  },
  kean: () => {
    return window.location.host.includes('kean')
  },
  blazesports: () => {
    return window.location.host.includes('blazesports')
  },
  blackboard: (user) =>
    (user ? user.data?.opt?.lti?.iss : localStorage.getItem('ltiSource')) ===
    'https://blackboard.com'
}

export const ID = {
  create: () => {
    return Math.random().toString(36).substr(2, 9)
  }
}
